import { ClientAddressField } from '@/components/schedule/client-address-field';
import { useAuthStore } from '@/store/authStore';
import { LOCATION_MODE } from '@/types/location-mode';
import { BOOKING_STATUS } from '@/types/service';
import React, { useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { Field, StatRow, StatTile } from '@/components/ui/section-card';
import { ServiceCoverCarousel } from '@/components/schedule/service-cover-carousel';
import { ScheduleGrade } from '@/components/schedule/schedule-grade';
import { useScheduleGrade } from '@/components/schedule/use-schedule-grade';
import {
  useAvailability,
  useBookableServiceDetail,
  useCreateAppointment,
} from '@/hooks/use-appointments';
import { useCreateRecurrence, usePreviewRecurrence } from '@/hooks/use-recurrences';
import { useSubscriptionStatus } from '@/hooks/use-subscriptions';
import { SubscriptionPlanCard } from '@/components/schedule/subscription-plan-card';
import { APPOINTMENT_STATUS, UnavailableReason } from '@/types/appointment';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { UNAVAILABLE_REASON_COPY } from '@/constants/availability-reason';
import { useToastStore } from '@/store/toastStore';
import { getApiErrorMessage } from '@/utils/api-error';
import { formatCurrency } from '@/utils/format-currency';
import { formatDuration, formatSlotTime } from '@/utils/format-appointment';
import { useThemedHex } from '@/hooks/use-theme-colors';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { KEYBOARD_BOTTOM_OFFSET } from '@/components/dashboard-scroll-view';

const DEFAULT_BRAND = '#FD5001';
const HORIZON_DAYS = 30;

/** bookingStatus do serviço → motivo que o cliente lê. */
function reasonFor(status: BOOKING_STATUS): UnavailableReason {
  switch (status) {
    case BOOKING_STATUS.CUSTOM_DURATION:
      return 'CUSTOM_DURATION';
    case BOOKING_STATUS.PAUSED:
      return 'PAUSED';
    default:
      return 'NO_GRID';
  }
}

/** Motivo específico de não haver horário, em vez de um vazio genérico. */
function UnavailableNotice({ reason }: { reason: UnavailableReason }) {
  const copy = UNAVAILABLE_REASON_COPY[reason];
  return (
    <View
      className="items-center gap-2 rounded-2xl bg-neutral-50 p-6"
      style={{ borderCurve: 'continuous' }}
    >
      <Ionicons className="text-neutral-400" name="calendar-clear-outline" size={36} />
      <ThemedText className="text-center font-semibold">{copy.title}</ThemedText>
      <ThemedText className="text-center text-sm text-neutral-500">{copy.description}</ThemedText>
    </View>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <View className="flex-1 items-center justify-center gap-3 bg-neutral-50 p-8">{children}</View>
  );
}

export default function ServiceBookingScreen() {
  const th = useThemedHex();
  const { serviceId } = useLocalSearchParams<{ serviceId: string }>();
  const { addToast } = useToastStore();

  const { data: service, isLoading, error } = useBookableServiceDetail(serviceId);
  const createAppointment = useCreateAppointment();
  const createRecurrence = useCreateRecurrence();
  const previewRecurrence = usePreviewRecurrence();

  const isSubscription = !!service?.isSubscription;
  const askAddress = service?.locationMode === LOCATION_MODE.CLIENT_ADDRESS;
  const savedAddress = useAuthStore((s) => s.user?.clientProfile?.address ?? null);
  const [address, setAddress] = useState('');
  const typedAddress = askAddress ? address.trim() || undefined : undefined;
  // Assinatura é uma só: o cliente paga o ciclo e marca dentro da cota.
  const quotaTerms = service?.quotaTerms ?? null;
  const subscription = useSubscriptionStatus(quotaTerms ? serviceId : undefined);
  const quotaActive = !!subscription.data?.active;

  // Montar a agenda que se repete é conveniência de quem ainda vai assinar: ele
  // assina já com as sessões pré-marcadas. Depois de assinante ele não precisa
  // marcar tudo de uma vez — cada sessão sai da cota quando ele quiser.
  const canRepeat = isSubscription ? !quotaActive : service?.recurringBookingMode === 'AUTO';

  // Mesma agenda do contrato e da página pública: repetir, liberar uma data,
  // trocar por outra. Antes de assinar, ela nasce repetindo — o que ele monta
  // é a agenda com que vai entrar no plano.
  const grade = useScheduleGrade({
    serviceId,
    preview: previewRecurrence.mutateAsync,
    canRepeat,
    isSubscription,
    // Assinante ativo marca sessão da cota; montar plano de novo seria pedir
    // um contrato que ele já tem.
    alreadySubscribed: quotaActive,
    quota: quotaTerms?.sessions ?? null,
    quotaPeriod: quotaTerms?.period ?? QUOTA_PERIOD.MONTH,
    singleWhenOnce: true,
    onWarn: (message) => addToast(message, 'warning'),
  });
  const selected = grade.selected;

  const range = useMemo(() => {
    const from = new Date();
    const to = new Date(from.getTime() + HORIZON_DAYS * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  const bookingOn = service?.bookingStatus === BOOKING_STATUS.BOOKABLE;
  const availability = useAvailability({
    providerId: service?.providerId,
    serviceId: bookingOn ? serviceId : undefined,
    from: range.from,
    to: range.to,
  });

  const brand = service?.brandColor || DEFAULT_BRAND;

  if (isLoading) {
    return (
      <Centered>
        <ActivityIndicator size="large" color={DEFAULT_BRAND} />
      </Centered>
    );
  }

  if (error || !service) {
    return (
      <Centered>
        <Ionicons className="text-neutral-400" name="alert-circle-outline" size={48} />
        <ThemedText className="text-center font-bold text-xl">Serviço indisponível</ThemedText>
        <ThemedText className="text-center text-neutral-500">
          Este serviço não existe ou não está mais disponível.
        </ThemedText>
        <PressableScale
          onPress={() => router.back()}
          className="mt-2 rounded-full px-6 py-3"
          style={{ backgroundColor: brand }}
        >
          <ThemedText className="font-bold text-white">Voltar</ThemedText>
        </PressableScale>
      </Centered>
    );
  }

  const priceLabel =
    service.priceDisplayMode === 'hidden' || service.price == null
      ? 'Valor sob consulta'
      : service.priceDisplayMode === 'from'
        ? `a partir de ${formatCurrency(service.price)}`
        : formatCurrency(service.price);

  // No plano o valor é do ciclo, não da sessão — dizer só "R$ 200" faria o
  // cliente ler como preço de um atendimento.
  const planPriceLabel =
    service.price == null || service.priceDisplayMode === 'hidden'
      ? null
      : `${formatCurrency(service.price)} ${
          service.recurringTerms?.billingInterval === 'WEEKLY' ? 'por semana' : 'por mês'
        }`;

  const days = availability.data?.days ?? [];
  const hasSlots = days.some((d) => d.available && d.slots.length > 0);
  const hasPhotos = (service.photos?.length ?? 0) > 0;

  const handleConfirmRecurrence = () => {
    const dto = grade.dto;
    if (!dto) return;
    createRecurrence.mutate(
      { ...dto, address: typedAddress },
      {
        onSuccess: () => {
          addToast(
            isSubscription
              ? 'Assinatura solicitada! O prestador vai confirmar.'
              : 'Recorrência solicitada! O prestador vai confirmar.',
            'success',
          );
          router.replace('/(tabs)/(client)/appointments');
        },
        onError: (err: any) => {
          addToast(getApiErrorMessage(err, 'Não foi possível enviar a recorrência.'), 'error');
          void availability.refetch();
        },
      },
    );
  };

  const handleConfirm = () => {
    // Assinatura (ainda não assinada) sempre entra pelo plano, repetindo ou
    // só nas datas escolhidas. Sessão avulsa é agendamento direto.
    if (grade.dto) return handleConfirmRecurrence();
    if (!selected) return;
    createAppointment.mutate(
      { serviceId: service.id, startAt: selected.startAt, address: typedAddress },
      {
        onSuccess: (appointment) => {
          // A sessão acabou de gastar cota: o saldo na volta tem que ser o novo.
          if (quotaTerms) void subscription.refetch();
          const pending = appointment.status === APPOINTMENT_STATUS.PENDING;
          addToast(
            pending
              ? 'Solicitação enviada! Aguarde a confirmação do prestador.'
              : 'Horário confirmado! 🎉',
            'success',
          );
          router.replace('/(tabs)/(client)/appointments');
        },
        onError: (err: any) => {
          addToast(
            getApiErrorMessage(err, 'Não foi possível marcar. Escolha outro horário.'),
            'error',
          );
          void availability.refetch();
          grade.clear();
        },
      },
    );
  };

  const confirmPending = grade.dto ? createRecurrence.isPending : createAppointment.isPending;
  // Cota esgotada: o backend recusaria de qualquer jeito, então o botão já diz.
  const sessionCount = grade.plan?.totalSessions ?? 0;
  const sessionWord = sessionCount === 1 ? 'sessão' : 'sessões';
  const quotaExhausted =
    !!quotaTerms && quotaActive && (subscription.data?.usage?.remaining ?? 0) <= 0;
  // Repetir só confirma com a agenda montada e válida — o que a prévia diz.
  const asPlan = !!grade.dto;
  const canConfirm = asPlan
    ? !!grade.plan?.valid && !grade.overQuota && !confirmPending
    : !!selected && !confirmPending && !quotaExhausted;
  const confirmLabel = quotaExhausted
    ? 'Cota do período esgotada'
    : asPlan
      ? grade.plan
        ? isSubscription
          ? `Assinar e deixar marcado · ${sessionCount} ${sessionWord}`
          : `Pedir recorrência · ${sessionCount} ${sessionWord}`
        : 'Montando a agenda…'
      : selected
        ? `Confirmar ${formatSlotTime(selected.startAt)}`
        : 'Selecione um horário';

  return (
    <View className="flex-1 bg-neutral-50">
      <KeyboardAwareScrollView
        bottomOffset={KEYBOARD_BOTTOM_OFFSET} keyboardShouldPersistTaps="handled" contentContainerStyle={{ paddingBottom: 40 }}>
        {/* Sem PublicBrandHeader aqui: esta rota vive dentro do shell do app
            (sidebar + header com logo e menu do usuário) — duplicaria a marca. */}
        {/* Seta na mesma linha do bloco de infos; as fotos vêm logo abaixo. */}
        <View className="flex-row items-start gap-2 px-4 pt-4">
          <Pressable
            onPress={() => router.back()}
            hitSlop={12}
            className="-ml-1 mt-0.5 p-1"
            accessibilityLabel="Voltar"
          >
            <Ionicons className="text-neutral-900" name="arrow-back" size={24} />
          </Pressable>

          <View className="flex-1 gap-1">
            <ThemedText className="font-bold text-2xl leading-8">{service.name}</ThemedText>
            <View className="flex-row items-center gap-1.5">
              <Ionicons className="text-neutral-500" name="person-circle-outline" size={15} />
              <ThemedText className="font-medium text-sm text-neutral-500">
                {service.providerName}
              </ThemedText>
            </View>
            <ThemedText className="mt-1 font-bold text-xl" style={{ color: brand }}>
              {priceLabel}
            </ThemedText>
          </View>
        </View>

        {/* Capa: só existe se o serviço tiver fotos (carrossel de cards). */}
        {hasPhotos && (
          <View className="pt-5">
            <ServiceCoverCarousel photos={service.photos} />
          </View>
        )}

        <View className="gap-4 px-4 pt-6">
          {/* Sobre o serviço */}
          {(!!service.description ||
            !!service.durationMinutes ||
            service.slotCapacity > 1 ||
            !!service.address) && (
            <View className="gap-3 rounded-2xl bg-card p-5" style={{ borderCurve: 'continuous' }}>
              <ThemedText className="font-bold text-base">Sobre o serviço</ThemedText>
              {!!service.description && (
                <ThemedText className="text-base leading-6 text-neutral-600">
                  {service.description}
                </ThemedText>
              )}
              <View className="gap-4">
                {(!!service.durationMinutes || service.slotCapacity > 1) && (
                  <StatRow>
                    {!!service.durationMinutes && (
                      <StatTile label="Duração" value={formatDuration(service.durationMinutes)} />
                    )}
                    {service.slotCapacity > 1 && (
                      <StatTile label="Vagas por horário" value={`Até ${service.slotCapacity}`} />
                    )}
                  </StatRow>
                )}
                {!!service.address && <Field label="Local" value={service.address} />}
              </View>
            </View>
          )}

          {/* Agendamento */}
          <View className="gap-4 rounded-2xl bg-card p-5" style={{ borderCurve: 'continuous' }}>
            <ThemedText className="font-bold text-base">Escolha um horário</ThemedText>

            {/* O card carrega os termos do plano, o saldo do período e o botão
                de assinar sem montar agenda nenhuma. */}
            {quotaTerms && (
              <SubscriptionPlanCard
                brand={brand}
                terms={quotaTerms}
                priceLabel={planPriceLabel}
                status={subscription.data}
                loading={subscription.isLoading}
              />
            )}

            {!bookingOn ? (
              <UnavailableNotice reason={reasonFor(service.bookingStatus)} />
            ) : availability.isLoading ? (
              <View className="items-center py-8">
                <ActivityIndicator color={brand} />
              </View>
            ) : hasSlots ? (
              <>
                <ScheduleGrade
                  grade={grade}
                  days={days}
                  brand={brand}
                  showCapacity={service.slotCapacity > 1}
                />

                {askAddress && (!!selected || !!grade.dto) && (
                  <ClientAddressField value={address} onChange={setAddress} saved={savedAddress} />
                )}

                <PressableScale
                  haptic
                  disabled={!canConfirm}
                  onPress={handleConfirm}
                  className="mt-2 w-full flex-row items-center justify-center gap-2 rounded-full py-4"
                  style={{
                    backgroundColor: canConfirm ? brand : th('#D4D4D4'),
                    borderCurve: 'continuous',
                    boxShadow: canConfirm ? '0 6px 20px rgba(23,17,10,0.22)' : undefined,
                  }}
                >
                  {confirmPending ? (
                    <ActivityIndicator color="#fff" />
                  ) : (
                    <>
                      <Ionicons name="checkmark-circle" size={20} color="#fff" />
                      <ThemedText className="font-bold text-base text-white">
                        {confirmLabel}
                      </ThemedText>
                    </>
                  )}
                </PressableScale>
              </>
            ) : (
              <UnavailableNotice reason={availability.data?.reason ?? 'NO_SLOTS_IN_RANGE'} />
            )}
          </View>
        </View>
      </KeyboardAwareScrollView>
    </View>
  );
}
